import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { CheckCircle2, XCircle, Trash2, Loader2 } from 'lucide-react'
import { storeAppleCredentials, deleteCredentials } from '@/lib/mobile-api'

interface CredentialSetupProps {
  appleConfigured: boolean
  onUpdate: () => void
}

export function CredentialSetup({ appleConfigured, onUpdate }: CredentialSetupProps) {
  const { t } = useTranslation('mobile')
  const [isExpanded, setIsExpanded] = useState(!appleConfigured)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    asc_key_id: '',
    asc_issuer_id: '',
    asc_key_content: '',
    apple_team_id: '',
    apple_team_type: 'INDIVIDUAL' as string,
    company_name: '',
  })

  const isValidKeyId = /^[A-Z0-9]{10}$/.test(form.asc_key_id)
  const isValidIssuerId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(form.asc_issuer_id)
  const isValidP8 = form.asc_key_content.includes('-----BEGIN PRIVATE KEY-----') && form.asc_key_content.includes('-----END PRIVATE KEY-----')
  const isValidTeamId = /^[A-Z0-9]{10}$/.test(form.apple_team_id)
  const formValid = isValidKeyId && isValidIssuerId && isValidP8 && isValidTeamId && !!form.apple_team_type

  const handleSave = useCallback(async () => {
    setError(null)
    setSaving(true)
    try {
      await storeAppleCredentials(form)
      setIsExpanded(false)
      onUpdate()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }, [form, onUpdate])

  const handleDelete = useCallback(async () => {
    setSaving(true)
    try {
      await deleteCredentials('apple')
      setForm({ asc_key_id: '', asc_issuer_id: '', asc_key_content: '', apple_team_id: '', apple_team_type: 'INDIVIDUAL', company_name: '' })
      setIsExpanded(true)
      onUpdate()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }, [onUpdate])

  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <div>
              <CardTitle className="text-base">{t('credentials.apple')}</CardTitle>
              <CardDescription>{t('credentials.appleDescription')}</CardDescription>
            </div>
          </div>
          {appleConfigured ? (
            <Badge variant="default" className="bg-green-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              {t('credentials.connected')}
            </Badge>
          ) : (
            <Badge variant="secondary">
              <XCircle className="h-3 w-3 mr-1" />
              {t('credentials.notConfigured')}
            </Badge>
          )}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="space-y-4">
          {!appleConfigured && (
            <div className="rounded-lg bg-muted p-4 text-sm space-y-2">
              <p className="font-medium">{t('credentials.apiKeyInstructions.title')}</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li><a href="https://appstoreconnect.apple.com/access/integrations/api" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('credentials.apiKeyInstructions.step1')}</a></li>
                <li>{t('credentials.apiKeyInstructions.step2')}</li>
                <li>{t('credentials.apiKeyInstructions.step3')}</li>
                <li><a href="https://developer.apple.com/account" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t('credentials.apiKeyInstructions.step4')}</a></li>
              </ol>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="asc_key_id">{t('credentials.fields.keyId')}</Label>
              <Input
                id="asc_key_id"
                placeholder="XXXXXXXXXX"
                value={form.asc_key_id}
                onChange={(e) => setForm({ ...form, asc_key_id: e.target.value.toUpperCase() })}
                disabled={appleConfigured}
              />
              {form.asc_key_id && !isValidKeyId && <p className="text-xs text-destructive">{t('credentials.validation.keyIdFormat')}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="asc_issuer_id">{t('credentials.fields.issuerId')}</Label>
              <Input
                id="asc_issuer_id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={form.asc_issuer_id}
                onChange={(e) => setForm({ ...form, asc_issuer_id: e.target.value })}
                disabled={appleConfigured}
              />
              {form.asc_issuer_id && !isValidIssuerId && <p className="text-xs text-destructive">{t('credentials.validation.issuerIdFormat')}</p>}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="apple_team_id">{t('credentials.fields.teamId')}</Label>
              <Input
                id="apple_team_id"
                placeholder="ABCD1234EF"
                value={form.apple_team_id}
                onChange={(e) => setForm({ ...form, apple_team_id: e.target.value.toUpperCase() })}
                disabled={appleConfigured}
              />
              {form.apple_team_id && !isValidTeamId && <p className="text-xs text-destructive">{t('credentials.validation.teamIdFormat')}</p>}
            </div>
            <div className="space-y-2">
              <Label>{t('credentials.fields.teamType')}</Label>
              <Select
                value={form.apple_team_type}
                onValueChange={(value) => setForm({ ...form, apple_team_type: value })}
                disabled={appleConfigured}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INDIVIDUAL">{t('credentials.fields.teamTypeIndividual')}</SelectItem>
                  <SelectItem value="COMPANY_OR_ORGANIZATION">{t('credentials.fields.teamTypeCompany')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company_name">{t('credentials.fields.companyName')}</Label>
            <Input
              id="company_name"
              placeholder="Your Company Inc."
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              disabled={appleConfigured}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="asc_key_content">{t('credentials.fields.apiKeyContent')}</Label>
            <Textarea
              id="asc_key_content"
              placeholder={t('credentials.fields.apiKeyPlaceholder')}
              rows={4}
              className="font-mono text-xs"
              value={form.asc_key_content}
              onChange={(e) => setForm({ ...form, asc_key_content: e.target.value })}
              disabled={appleConfigured}
            />
            {form.asc_key_content && !isValidP8 && <p className="text-xs text-destructive">{t('credentials.validation.apiKeyFormat')}</p>}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-2">
            {!appleConfigured ? (
              <Button onClick={handleSave} disabled={saving || !formValid}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('credentials.saveCredentials')}
              </Button>
            ) : (
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={saving}>
                <Trash2 className="h-4 w-4 mr-2" />
                {t('credentials.remove')}
              </Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}
